/**
 * Application-wide configuration.
 *
 * Anything a client might reasonably want changed without a code
 * review — brand name, currency, squad rules — lives here rather
 * than being scattered through components.
 */

export const APP_CONFIG = {
  /** Product name shown in the sidebar and document title. */
  name: '1XI',

  /** Storage key for the persisted squad state. Bump to invalidate. */
  storageKey: 'matchday-state',

  /**
   * Currency used for player prices, team value and bank balance.
   * `locale` and `code` drive Intl formatting; `symbol` is used for
   * the compact "£4.5m" style price labels.
   */
  currency: {
    code: 'GBP',
    symbol: '£',
    locale: 'en-GB',
  },
} as const;

/**
 * Contest rules, as the API enforces them.
 *
 * This is daily fantasy, not season management: a starting eleven plus a bench
 * of four per contest, no budget. Shapes live in `src/lib/formations.ts`.
 */
export const CONTEST_RULES = {
  /** Players fielded — the only ones who score. */
  teamSize: 11,
  /** Substitutes, swapped into the eleven one at a time. */
  benchSize: 4,
  /** Captain scores double; the vice takes over if the captain does not play. */
  captainMultiplier: 2,
} as const;

/**
 * The competition the app is currently playing.
 *
 * The API scopes players, fixtures and gameweeks under a series
 * (`/football/series/{seriesId}/...`) but exposes no "current series" endpoint,
 * so the frontend has to be told which one. Overridable per environment;
 * 900001 is the series created by the API's `db:seed:fe` script.
 *
 * An empty value counts as unset — it is what copying `.env.example` leaves —
 * rather than becoming series 0.
 */
export const CURRENT_SERIES_ID = Number(import.meta.env.VITE_SERIES_ID?.trim() || 900001);

